import React from 'react';
import { useIntl } from 'react-intl';
import {
  ApiFilterOperation,
  EmptyComponent,
  TableCells,
  TableColumn,
  TableColumnAlign,
  eqFilterParams,
} from '@eas/common-web';
import { useCustomFilterAutocomplete } from '@composite/autocomplete-item/autocomplete-item-with-deactivated-items';
import { Redirect } from '@composite/redirect/redirect';
import { autocompleteColumnMapper } from '@components/form/misc/label-mappers';
import { CourtFile } from '@models';
import { EvidenceBrowserUrl, Role } from '@enums';
import { useJudges } from '../../judge/judge-api';
import { useUserRoles } from '../../role/role-api';
import { useCourtFileColumns } from '../court-file-columns';

export function useColumns(): TableColumn<CourtFile>[] {
  const intl = useIntl();
  const {
    columnJid,
    columnDeliveryDate,
    columnOpponent,
    columnProposers,
    columnType,
  } = useCourtFileColumns();
  const useIndexes = () =>
    useUserRoles({
      ...eqFilterParams({ field: 'role.id', value: Role.INDEX }),
      include: ['DEACTIVATED', 'DELETED'],
    });

  return [
    {
      datakey: 'id',
      name: intl.formatMessage({
        id: 'ES__COURT_FILES_REGISTER_NEW__TABLE__COLUMN__REDIRECT',
        defaultMessage: 'Přesměrování',
      }),
      NameComponent: EmptyComponent,
      width: 50,
      CellComponent: function Cell(props) {
        return (
          <>
            <Redirect
              url={EvidenceBrowserUrl.COURT_FILES_REGISTER_NEW}
              id={props.value}
              showRedirectInCurrent={false}
            />
          </>
        );
      },
      align: TableColumnAlign.LEFT,
    },
    columnDeliveryDate,
    {
      datakey: 'incomingNumber',
      name: intl.formatMessage({
        id: 'ES__COURT_FILES_REGISTER_NEW__TABLE__COLUMN__INCOMING_NUMBER',
        defaultMessage: 'Došlé číslo',
      }),
      width: 100,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'number.value',
      sortkey: 'number.valueSort',
      name: intl.formatMessage({
        id: 'ES__COURT_FILES_REGISTER_NEW__TABLE__COLUMN__FILE_NUMBER',
        defaultMessage: 'Spisová značka',
      }),
      width: 120,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
    },
    columnJid,
    {
      datakey: 'judge',
      filterDialogSpan: 2,
      filterkey: 'judge.id',
      sortkey: 'judge.name',
      name: intl.formatMessage({
        id: 'ES__COURT_FILES_REGISTER_NEW__TABLE__COLUMN__JUDGE',
        defaultMessage: 'Soudce zpravodaj',
      }),
      width: 200,
      valueMapper: autocompleteColumnMapper,
      CellComponent: TableCells.TextCell,
      FilterComponent: useCustomFilterAutocomplete({
        useSource: () => useJudges({ include: ['DEACTIVATED'] }),
      }),
      filterOperation: ApiFilterOperation.EQ,
      sortable: true,
      filterable: true,
    },
    columnProposers,
    columnOpponent,
    columnType,
    {
      datakey: 'indexUserRole',
      filterDialogSpan: 2,
      filterkey: 'indexUserRole.id',
      sortkey: 'indexUserRole.name',
      name: intl.formatMessage({
        id: 'ES__COURT_FILES_REGISTER_NEW__TABLE__COLUMN__INDEX',
        defaultMessage: 'Vedoucí rejstříku',
      }),
      width: 200,
      valueMapper: autocompleteColumnMapper,
      CellComponent: TableCells.TextCell,
      FilterComponent: useCustomFilterAutocomplete({ useSource: useIndexes }),
      filterOperation: ApiFilterOperation.EQ,
      sortable: true,
      filterable: true,
    },
  ];
}
